(function () {
  'use strict';

  if (window.__cartDrawerUpsellInit) return;
  window.__cartDrawerUpsellInit = true;

  const SECTION_ID = 'cart-drawer-upsell-recommendations';
  const PLACEHOLDER_SELECTOR = '.cart-drawer__upsell-recommendations';
  const WRAPPER_SELECTOR = '[data-cart-drawer-upsell]';
  const CONTENT_SELECTOR = '[data-cart-drawer-upsell-content]';
  const LIST_SELECTOR = '.cart-drawer__upsell-list';
  const CAROUSEL_SELECTOR = '.cart-drawer__upsell-carousel';
  const SCROLL_SELECTOR = '.cart-drawer__upsell-scroll';
  const CHEVRON_SVG =
    '<svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-chevron-right" viewBox="0 0 192 192"><path d="m72 36 60 60-60 60" stroke="currentColor" stroke-width="10" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  let activeRequest = null;

  function updateCarouselArrows(carousel) {
    const scroll = carousel?.querySelector(SCROLL_SELECTOR);
    const prev = carousel?.querySelector('[data-cart-drawer-upsell-arrow-prev]');
    const next = carousel?.querySelector('[data-cart-drawer-upsell-arrow-next]');

    if (!scroll || !prev || !next) return;

    const maxScroll = scroll.scrollWidth - scroll.clientWidth;
    const hasOverflow = maxScroll > 1;

    prev.classList.toggle('is-hidden', !hasOverflow || scroll.scrollLeft <= 1);
    next.classList.toggle('is-hidden', !hasOverflow || scroll.scrollLeft >= maxScroll - 1);
  }

  function getScrollStep(scroll) {
    const paddingLeft = parseFloat(getComputedStyle(scroll).paddingLeft) || 0;
    return scroll.clientWidth - paddingLeft;
  }

  function initCarouselNav(carousel) {
    if (!carousel || carousel.dataset.navInit === 'true') return;

    const scroll = carousel.querySelector(SCROLL_SELECTOR);
    const prev = carousel.querySelector('[data-cart-drawer-upsell-arrow-prev]');
    const next = carousel.querySelector('[data-cart-drawer-upsell-arrow-next]');

    if (!scroll || !prev || !next) return;

    carousel.dataset.navInit = 'true';

    const onScroll = () => updateCarouselArrows(carousel);

    prev.addEventListener('click', (event) => {
      event.preventDefault();
      const step = getScrollStep(scroll);
      const target = Math.max(scroll.scrollLeft - step, 0);
      scroll.scrollTo({ left: target, behavior: 'smooth' });
    });

    next.addEventListener('click', (event) => {
      event.preventDefault();
      const step = getScrollStep(scroll);
      const maxScroll = scroll.scrollWidth - scroll.clientWidth;
      const target = Math.min(scroll.scrollLeft + step, maxScroll);
      scroll.scrollTo({ left: target, behavior: 'smooth' });
    });

    scroll.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    document.addEventListener('theme:cart:open', onScroll);
    document.addEventListener('cart-drawer:refreshed', onScroll);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => updateCarouselArrows(carousel));
    });
  }

  function refreshCarouselNav() {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        document.querySelectorAll(CAROUSEL_SELECTOR).forEach((carousel) => {
          updateCarouselArrows(carousel);
        });
      });
    });
  }

  function getPlaceholders() {
    return document.querySelectorAll(PLACEHOLDER_SELECTOR);
  }

  function buildFetchUrl(productId, limit, intent) {
    const params = new URLSearchParams({
      product_id: productId,
      limit: limit,
      intent: intent,
      section_id: SECTION_ID,
    });

    return `${window.Shopify?.routes?.root || '/'}recommendations/products?${params.toString()}`;
  }

  function resetWrapper(wrapper) {
    if (!wrapper) return;

    const placeholder = wrapper.querySelector(PLACEHOLDER_SELECTOR);
    const heading = wrapper.querySelector('.cart-drawer__upsell-heading');
    const carousel = wrapper.querySelector(CAROUSEL_SELECTOR);
    const scroll = wrapper.querySelector('.cart-drawer__upsell-scroll');

    if (heading) heading.remove();
    if (carousel) carousel.remove();
    else if (scroll) scroll.remove();
    if (placeholder) placeholder.hidden = true;

    wrapper.hidden = true;
  }

  function renderWrapper(wrapper, placeholder, html, headingText) {
    resetWrapper(wrapper);

    const temp = document.createElement('div');
    temp.innerHTML = html.trim();

    const content = temp.querySelector(CONTENT_SELECTOR);
    const list = content?.querySelector(LIST_SELECTOR);

    if (!content || !list || !list.children.length) {
      return;
    }

    const heading = document.createElement('h4');
    heading.className = 'cart-drawer__upsell-heading';
    heading.textContent = headingText;

    const carousel = document.createElement('div');
    carousel.className = 'cart-drawer__upsell-carousel';

    const scroll = document.createElement('div');
    scroll.className = 'cart-drawer__upsell-scroll';
    scroll.appendChild(content);

    const arrows = document.createElement('div');
    arrows.className = 'cart-drawer__upsell-arrows';

    const prev = document.createElement('button');
    prev.type = 'button';
    prev.className = 'cart-drawer__upsell-arrow cart-drawer__upsell-arrow--prev is-hidden';
    prev.setAttribute('data-cart-drawer-upsell-arrow-prev', '');
    prev.setAttribute('aria-label', 'Previous products');
    prev.innerHTML = CHEVRON_SVG;

    const next = document.createElement('button');
    next.type = 'button';
    next.className = 'cart-drawer__upsell-arrow cart-drawer__upsell-arrow--next';
    next.setAttribute('data-cart-drawer-upsell-arrow-next', '');
    next.setAttribute('aria-label', 'Next products');
    next.innerHTML = CHEVRON_SVG;

    arrows.append(prev, next);
    carousel.append(scroll, arrows);

    wrapper.insertBefore(heading, placeholder);
    wrapper.insertBefore(carousel, placeholder);
    wrapper.hidden = false;

    initCarouselNav(carousel);
  }

  function loadPlaceholder(placeholder) {
    const wrapper = placeholder.closest(WRAPPER_SELECTOR);
    if (!wrapper) return;

    const productId = placeholder.dataset.productId;
    const limit = placeholder.dataset.limit || '4';
    const intent = placeholder.dataset.intent || 'related';
    const headingText = placeholder.dataset.upsellHeading || window.theme?.strings?.cart_upsell_heading || '';

    if (!productId) {
      resetWrapper(wrapper);
      return;
    }

    const url = buildFetchUrl(productId, limit, intent);

    if (activeRequest) {
      activeRequest.abort();
    }

    activeRequest = new AbortController();

    fetch(url, { signal: activeRequest.signal })
      .then((response) => {
        if (!response.ok) throw new Error('Recommendations request failed');
        return response.text();
      })
      .then((responseText) => {
        activeRequest = null;
        renderWrapper(wrapper, placeholder, responseText, headingText);
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        activeRequest = null;
        resetWrapper(wrapper);
      });
  }

  function syncUpsellPlaceholder() {
    const holder = document.querySelector('[data-cart-drawer-upsell-holder]');
    if (!holder) return;

    const placeholder = holder.querySelector(PLACEHOLDER_SELECTOR);
    const config = document.querySelector('[data-cart-price-holder] [data-cart-upsell-config]');

    if (!placeholder) {
      holder.classList.add('hidden');
      return;
    }

    if (config) {
      placeholder.dataset.productId = config.dataset.productId;
      placeholder.dataset.limit = config.dataset.limit;
      placeholder.dataset.intent = config.dataset.intent;
      placeholder.dataset.upsellHeading = config.dataset.upsellHeading;
    }

    holder.classList.toggle('hidden', !config && !placeholder.dataset.productId);
  }

  function loadAll() {
    syncUpsellPlaceholder();
    getPlaceholders().forEach(loadPlaceholder);
    document.dispatchEvent(new CustomEvent('cart-drawer:refreshed', { bubbles: true }));
  }

  function onReady() {
    loadAll();

    document.addEventListener('theme:cart:added', loadAll);
    document.addEventListener('theme:cart:loaded', loadAll);
    document.addEventListener('theme:cart:open', refreshCarouselNav);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onReady);
  } else {
    onReady();
  }
})();
